import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { JSX } from "react";

import { useThemeMode, type ThemeMode } from "../../styles/ThemeModeProvider.js";
import { MoonIcon, SunIcon } from "../../pages/login/icons.js";

/**
 * The two-state light/dark toggle — the customer support portal's
 * `ThemeToggleSegment`, rebuilt in MUI (the reference is Tailwind, and its
 * glyphs come from `lucide-react`, which the sign-in screen deliberately does
 * not carry; see `pages/login/icons.tsx`).
 *
 * A `radiogroup` of two radios, not a checkbox or a switch: both states are
 * named and either can be chosen directly, which is what the control looks
 * like and what a screen reader should hear.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY THERE IS A `surface` PROP, rather than two components.
 *
 * This control now appears on two surfaces with genuinely different colour
 * rules, and getting the second one wrong is invisible until you switch mode:
 *
 *   • `"fixed-light"` — the sign-in screen's top bar. That strip is WHITE IN
 *     BOTH MODES (it carries the dark-ink logo), so the control reads from
 *     `common.black` alphas. Anything keyed to `text.*` would turn
 *     white-on-white the moment dark mode is on. See `pages/login/TopBar.tsx`.
 *
 *   • `"themed"` — the account menu, which sits on `background.paper` and
 *     DOES follow the theme. Here the black alphas are the bug: they vanish
 *     against a dark menu.
 *
 * One control with a named surface beats two near-identical files that drift,
 * and beats a single implementation that is silently wrong on one of them.
 * ─────────────────────────────────────────────────────────────────────────
 */
export function ThemeToggleSegment({
  surface = "fixed-light",
}: {
  surface?: "fixed-light" | "themed";
} = {}): JSX.Element {
  const { mode, setMode } = useThemeMode();
  const fixed = surface === "fixed-light";

  const items: ReadonlyArray<{ value: ThemeMode; label: string; icon: JSX.Element }> = [
    { value: "light", label: "Light", icon: <SunIcon /> },
    { value: "dark", label: "Dark", icon: <MoonIcon /> },
  ];

  /** The "ink" this control draws its inactive states from: literal black on
   * the permanently-white bar, the theme's own foreground everywhere else. */
  const ink = (t: import("@mui/material/styles").Theme): string =>
    fixed ? t.palette.common.black : t.palette.text.primary;

  return (
    <Box
      role="radiogroup"
      aria-label="Theme"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.25,
        p: 0.25,
        borderRadius: 999,
        border: 1,
        borderColor: (t) => alpha(ink(t), 0.1),
        bgcolor: (t) => alpha(ink(t), 0.04),
      }}
    >
      {items.map(({ value, label, icon }) => {
        const active = mode === value;
        return (
          <Box
            key={value}
            component="button"
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={label}
            title={label}
            onClick={() => setMode(value)}
            sx={{
              display: "grid",
              placeItems: "center",
              height: 28,
              width: 28,
              p: 0,
              border: 0,
              borderRadius: 999,
              cursor: "pointer",
              transition: "background-color 150ms, color 150ms",
              bgcolor: active ? "accent.700" : "transparent",
              color: (t) => (active ? t.palette.accent.contrastText : alpha(ink(t), 0.5)),
              boxShadow: (t) =>
                active ? `0 4px 10px -4px ${alpha(t.palette.accent[700], 0.7)}` : "none",
              "&:hover": {
                bgcolor: (t) => (active ? t.palette.accent[700] : alpha(ink(t), 0.06)),
                color: (t) => (active ? t.palette.accent.contrastText : alpha(ink(t), 0.8)),
              },
              "&:focus-visible": {
                outline: (t) => `2px solid ${t.palette.accent[600]}`,
                outlineOffset: 2,
              },
            }}
          >
            {icon}
          </Box>
        );
      })}
    </Box>
  );
}
